import { Moon, Sun, Monitor } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useTheme } from "@/hooks/use-theme";

interface ThemeToggleProps {
  className?: string;
  showLabel?: boolean;
}

export function ThemeToggle({ className = "", showLabel = false }: ThemeToggleProps) {
  const { theme, setTheme } = useTheme();

  const cycleTheme = () => {
    if (theme === 'light') {
      setTheme('dark');
    } else if (theme === 'dark') {
      setTheme('system');
    } else {
      setTheme('light');
    }
  };

  const getThemeIcon = () => {
    if (theme === 'system') return <Monitor className="h-4 w-4" />;
    return theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />;
  };

  const getThemeLabel = () => {
    if (theme === 'system') return 'System';
    return theme === 'dark' ? 'Dark' : 'Light';
  };

  const getButtonStyles = () => {
    if (theme === 'system') return 'bg-secondary border-border text-secondary-foreground';
    if (theme === 'dark') return 'bg-primary/15 border-primary/30 text-primary';
    return 'bg-background border-border text-foreground';
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {showLabel && (
        <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
          Theme
        </span>
      )}
      <Button
        onClick={cycleTheme}
        variant="ghost"
        size="sm"
        className={`h-9 border px-3 transition-all duration-200 hover:scale-105 active:scale-95 ${getButtonStyles()}`}
        aria-label={`Current theme: ${getThemeLabel()}. Click to change.`}
      >
        {getThemeIcon()}
        <span className="text-xs font-bold hidden sm:inline">
          {getThemeLabel()}
        </span>
      </Button>
    </div>
  );
}
